import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { api } from '../services/api';
import { Card, Spinner, Alert } from '../components/ui';
import { Download, Calendar, User } from 'lucide-react';

const GalleryPage = () => {
  const { accessKey } = useAppStore();

  // Fetch posts

  // Filter posts to exclude posts with status draft
  const {
    data: posts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['posts'],
    queryFn: () => api.checkPostsReady(accessKey),
    enabled: !!accessKey,
  });

  const downloadImage = async (imageUrl, filename) => {
    try {
      // Fetch the image as a blob to avoid CORS issues
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'image.jpg';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download image:', error);
      // Fallback: open in new tab if blob download fails
      window.open(imageUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Spinner size='lg' />
      </div>
    );
  }

  if (error) {
    return <Alert variant='error'>Failed to load posts: {error.message}</Alert>;
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl font-bold text-gray-900'>Content Gallery</h1>
        <div className='text-sm text-gray-500'>{posts.length} posts available</div>
      </div>

      {posts.length === 0 ? (
        <Card>
          <Card.Body>
            <div className='text-center py-12'>
              <div className='text-4xl mb-4'>📸</div>
              <h3 className='text-lg font-medium text-gray-900 mb-2'>No posts available</h3>
              <p className='text-gray-600'>Posts will appear here once they are generated.</p>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {posts.length ? (
            posts
              .filter((post) => post.status !== 'draft')
              .map((post) => (
                <Card key={post.id} className='overflow-hidden'>
                  <div className='relative group'>
                    <img
                      src={post.path || post.imageUrl}
                      alt='Post content'
                      className='w-full h-64 object-cover'
                      loading='lazy'
                      style={{
                        objectFit: 'contain',
                      }}
                    />
                    <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center'>
                      <button
                        onClick={() => downloadImage(post.path || post.imageUrl, `post-${post.id}`)}
                        className='opacity-0 group-hover:opacity-100 bg-white text-gray-900 px-4 py-2 rounded-lg flex items-center space-x-2 transition-opacity duration-200'
                      >
                        <Download className='w-4 h-4' />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                </Card>
              ))
          ) : (
            <div>No posts available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
